/**
 * useCuttlefishVhal — thin context consumer.
 *
 * All VHAL state and logic live in `VhalGrpcServiceProvider`. This hook is
 * just a convenience wrapper so panels and feature hooks don't need to
 * import from the provider directly.
 *
 * IMPORTANT: The component tree must contain a `<VhalGrpcServiceProvider>`
 * ancestor (mounted in `EmulatorsProvider` for the portal; in the
 * vscode-ext webview's emulator context for the extension).
 */

import { useEffect, useState } from "react";
import { useCuttlefishVhalContext } from "../providers/VhalGrpcServiceProvider";
import type { VhalPropertyValue } from "../types/vhal";
import type { VhalService } from "../types/vhalService";

/**
 * Access the singleton VHAL service from context.
 * Throws if no `VhalGrpcServiceProvider` ancestor is mounted.
 */
export function useCuttlefishVhal(): VhalService {
  return useCuttlefishVhalContext();
}

/**
 * Monitor a specific VHAL property with periodic auto-updates.
 */
export function useVhalProperty(
  propertyId: number,
  intervalMs = 1000,
  enabled = true,
): VhalPropertyValue | null {
  const vhal = useCuttlefishVhal();
  const [value, setValue] = useState<VhalPropertyValue | null>(null);

  useEffect(() => {
    if (!enabled || !vhal.isAvailable) return;

    vhal.getProperty(propertyId).then(setValue);

    const unsubscribe = vhal.subscribeToProperty(propertyId, setValue, intervalMs);
    return unsubscribe;
  }, [propertyId, intervalMs, enabled, vhal]);

  return value;
}
